<template>
  <footer class="site_footer">
    <div class="site_footer__inner">
      <div class="site_footer__main">
        <div class="site_footer__sections">
          <!-- LINK -->
          <section class="site_footer__section">
            <h2 class="site_footer__heading">LINK</h2>
            <ul class="site_footer__list">
              <li>
                <a
                  class="site_footer__link"
                  href="https://reality.inc"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('common__operating_company') }}</a
                >
              </li>
              <li>
                <a
                  class="site_footer__link"
                  :href="footerLinks.help"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('common__faq') }}</a
                >
              </li>
              <li>
                <a
                  class="site_footer__link"
                  :href="footerLinks.inquiry"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('common__inquiry') }}</a
                >
              </li>
            </ul>
          </section>

          <!-- REALITY -->
          <section class="site_footer__section">
            <h2 class="site_footer__heading">REALITY</h2>
            <ul class="site_footer__list">
              <li>
                <a
                  class="site_footer__link"
                  :href="$t('termsOfUseUrl')"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('termsOfUse') }}</a
                >
              </li>
              <li>
                {{ $t('common__privacy_policy') }} (
                <a
                  class="site_footer__link"
                  :href="footerLinks.privacyPolicyIos"
                  target="_blank"
                  rel="noopener noreferrer"
                  >iOS</a
                >
                /
                <a
                  class="site_footer__link"
                  :href="footerLinks.privacyPolicyAndroid"
                  target="_blank"
                  rel="noopener noreferrer"
                  >Android</a
                >
                )
              </li>
              <li>
                <a
                  class="site_footer__link"
                  :href="$t('coinPolicyUrl')"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('coinPolicy') }}</a
                >
              </li>
              <li v-if="isJapan">
                <a
                  class="site_footer__link"
                  href="https://reality.app/legal/sct.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('sctPolicy') }}</a
                >
              </li>
              <li v-if="isJapan">
                <a
                  class="site_footer__link"
                  href="https://reality.app/r/legal/premium"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('premium__label__terms') }}</a
                >
              </li>
              <li v-if="isJapan">
                <a
                  class="site_footer__link"
                  href="https://reality.app/r/legal/membership"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('membership_onboarding__label__terms_of_service') }}</a
                >
              </li>
              <li>
                <a
                  class="site_footer__link"
                  :href="footerLinks.communityGuideline"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('about_reality__list__community_guideline') }}</a
                >
              </li>
              <li>
                <a
                  class="site_footer__link"
                  :href="footerLinks.derivativeGuideline"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('about_reality__list__derivative_guidelines') }}</a
                >
              </li>
              <li>
                <a
                  class="site_footer__link"
                  :href="footerLinks.forSafeAndSecureUse"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ $t('about_reality__list__for_safe_and_secure_use') }}</a
                >
              </li>
            </ul>
          </section>

          <!-- 運営会社（ロゴ＋住所） -->
          <section class="site_footer__section site_footer__section--company">
            <h2 class="site_footer__heading">
              {{ $t('common__operating_company') }}
            </h2>
            <div class="site_footer__company">
              <a
                class="site_footer__logo_link"
                href="https://reality.inc"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SiteLogo :height="22" color="#ffffff" />
                <span class="site_footer__sr_only">REALITY</span>
              </a>
              <p v-if="isJapan" class="site_footer__address">
                {{ companyAddress }}
              </p>
            </div>
          </section>
        </div>

        <div class="site_footer__bottom">
          <p class="site_footer__copyright">© REALITY, Inc.</p>
          <LocaleSwitcher />
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-property-decorator'
import SiteLogo from '@/components/Navigation/SiteLogo.vue'
import LocaleSwitcher from '@/components/Navigation/LocaleSwitcher.vue'
import { LANG_JA, LANG_KO } from '@/i18n'

// v2 の site-footer と同じ見た目のサイトフッターを表示
@Component({
  components: {
    SiteLogo,
    LocaleSwitcher,
  },
})
export default class SiteFooter extends Vue {
  readonly companyAddress =
    '東京都港区六本木六丁目11番1号\n六本木ヒルズゲートタワー'

  get isJapan(): boolean {
    return this.$i18n.locale === LANG_JA
  }

  get footerLinks() {
    const isJa = this.$i18n.locale === LANG_JA
    const isKo = this.$i18n.locale === LANG_KO
    return {
      help: isJa
        ? 'https://help.reality.app/hc/ja'
        : 'https://help.reality.app/hc/en-us',
      inquiry: isJa
        ? 'https://help.le.wrightflyer.net/?action=inquiry_form&app_id=258305124483241&official=1&lang=ja'
        : 'https://help.le.wrightflyer.net/?action=inquiry_form&app_id=258305124483241&official=1&lang=en',
      privacyPolicyIos: isJa
        ? 'https://reality.app/legal/privacy_policy_ios.html'
        : 'https://reality.app/legal/privacy_policy_ios_en.html',
      privacyPolicyAndroid: isJa
        ? 'https://reality.app/legal/privacy_policy_android.html'
        : 'https://reality.app/legal/privacy_policy_android_en.html',
      communityGuideline: isJa
        ? 'https://reality.app/guideline/community.html'
        : isKo
          ? 'https://reality.app/guideline/community_ko.html'
          : 'https://reality.app/guideline/community_en.html',
      derivativeGuideline: isJa
        ? 'https://reality.app/guideline/derivative_works.html'
        : isKo
          ? 'https://reality.app/guideline/derivative_works_ko.html'
          : 'https://reality.app/guideline/derivative_works_en.html',
      forSafeAndSecureUse: isJa
        ? 'https://reality.app/guideline/safety_ja.html'
        : isKo
          ? 'https://reality.app/guideline/safety_ko.html'
          : 'https://reality.app/guideline/safety_en.html',
    }
  }
}
</script>

<style scoped>
.site_footer {
  background-color: #0f0f0f;
  color: #ffffff;
}

.site_footer__inner {
  box-sizing: border-box;
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 16px;
}

.site_footer__main {
  display: flex;
  flex-direction: column;
  gap: 30px; /* gap-7.5 */
}

.site_footer__sections {
  display: flex;
  flex-direction: column;
  gap: 20px; /* gap-5 */
}
@media (min-width: 768px) {
  .site_footer__sections {
    flex-direction: row;
    justify-content: space-between;
    gap: 0;
  }
  .site_footer__section {
    flex: 1;
  }
}

.site_footer__section {
  display: flex;
  flex-direction: column;
  gap: 6px; /* gap-1.5 */
  width: 100%;
}
.site_footer__section--company {
  gap: 26px; /* gap-6.5 */
}

.site_footer__heading {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
}

.site_footer__list {
  list-style: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  border-left: 1px solid #464646;
  padding: 8px 16px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
}

.site_footer__link {
  color: inherit;
  text-decoration: none;
}
.site_footer__link:hover {
  text-decoration: underline;
}

.site_footer__company {
  display: flex;
  flex-direction: column;
  gap: 12px; /* gap-3 */
}

.site_footer__logo_link {
  display: inline-block;
  padding: 8px 0; /* py-2 */
}

.site_footer__address {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  white-space: pre-line;
}

.site_footer__bottom {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px; /* gap-4 */
}
@media (min-width: 768px) {
  .site_footer__bottom {
    justify-content: start;
  }
}

.site_footer__copyright {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
}

.site_footer__sr_only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
